/**
 * Shared AI Assistant Styles
 * 
 * All styled components for the unified AI Assistant panel.
 * Used across Synthora Hub, Dashboard, Workspace, Profile, etc.
 */

import styled, { keyframes, css } from 'styled-components';

// ============================================
// ANIMATIONS
// ============================================

export const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

export const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

export const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

export const typingDots = keyframes`
  0%, 20% { opacity: 0.3; }
  50% { opacity: 1; }
  80%, 100% { opacity: 0.3; }
`;

export const recordingPulse = keyframes`
  0%, 100% { transform: scale(0.8); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0.8; }
`;

// ============================================
// PANEL CONTAINERS
// ============================================

export const PanelOverlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 999;
  opacity: ${props => (props.$isOpen && !props.$isPinned) ? 1 : 0};
  pointer-events: ${props => (props.$isOpen && !props.$isPinned) ? 'auto' : 'none'};
  transition: opacity 0.2s ease;
`;

export const FloatingPanelContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  max-width: 100vw;
  height: 100vh;
  background: linear-gradient(180deg, #0f1629 0%, #0a0f1a 100%);
  border-left: 1px solid rgba(99, 102, 241, 0.2);
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  animation: ${props => props.$isOpen ? slideIn : slideOut} 0.25s ease-out forwards;
  
  ${props => !props.$isOpen && css`
    pointer-events: none;
  `}
`;

export const EmbeddedPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(180deg, #0f1629 0%, #0a0f1a 100%);
  overflow: hidden;
  border-left: 1px solid rgba(99, 102, 241, 0.2);
`;

// ============================================
// HEADER
// ============================================

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%);
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
`;

export const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const HeaderIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 22px;
    height: 22px;
    object-fit: contain;
  }
`;

export const TitleText = styled.div`
  h3 {
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    margin: 0;
  }
  
  span {
    font-size: 11px;
    color: rgba(165, 180, 252, 0.7);
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const IconButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: rgba(248, 250, 252, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

export const PinButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid ${props => props.$isPinned ? 'rgba(16, 185, 129, 0.4)' : 'rgba(255, 255, 255, 0.1)'};
  background: ${props => props.$isPinned ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.$isPinned ? '#10b981' : 'rgba(248, 250, 252, 0.7)'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  svg {
    transition: transform 0.2s ease;
    transform: ${props => props.$isPinned ? 'rotate(45deg)' : 'rotate(0deg)'};
  }
  
  &:hover {
    background: ${props => props.$isPinned ? 'rgba(16, 185, 129, 0.25)' : 'rgba(255, 255, 255, 0.1)'};
    border-color: ${props => props.$isPinned ? 'rgba(16, 185, 129, 0.6)' : 'rgba(255, 255, 255, 0.2)'};
  }
`;

export const SettingsButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: ${props => props.$active ? 'rgba(99, 102, 241, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.$active ? '#a5b4fc' : 'rgba(248, 250, 252, 0.7)'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.15);
    color: #a5b4fc;
    border-color: rgba(99, 102, 241, 0.3);
  }
`;

// ============================================
// CONTEXT BADGE
// ============================================

export const ContextBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 8px;
  margin: 0 16px 8px 16px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  
  .indicator {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #6366f1;
  }
  
  .text {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;
  }
`;

// ============================================
// MESSAGES AREA
// ============================================

export const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(99, 102, 241, 0.5);
    }
  }
`;

export const MessageGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  animation: ${fadeIn} 0.3s ease-out;
`;

export const MessageBubble = styled.div`
  padding: 14px 18px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.7;
  max-width: 92%;
  
  ${props => props.$isUser ? css`
    align-self: flex-end;
    background: rgba(99, 102, 241, 0.2);
    border: 1px solid rgba(99, 102, 241, 0.3);
    color: rgba(255, 255, 255, 0.95);
    border-bottom-right-radius: 4px;
  ` : css`
    align-self: flex-start;
    background: rgba(20, 25, 40, 0.9);
    border: 1px solid rgba(100, 150, 200, 0.1);
    color: rgba(255, 255, 255, 0.85);
    border-bottom-left-radius: 4px;
  `}
  
  /* Markdown styling */
  strong, b {
    color: #a5b4fc;
    font-weight: 600;
  }
  
  code {
    background: rgba(99, 102, 241, 0.15);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Consolas', 'Monaco', monospace;
    font-size: 12px;
    color: #c4b5fd;
  }
  
  ul, ol {
    margin: 10px 0;
    padding-left: 18px;
  }
  
  li {
    margin: 6px 0;
    padding-left: 4px;
    
    &::marker {
      color: rgba(165, 180, 252, 0.6);
    }
  }
  
  p {
    margin: 10px 0;
    
    &:first-child {
      margin-top: 0;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  /* Numbered steps styling */
  ol {
    counter-reset: step-counter;
    list-style: none;
    padding-left: 0;
    
    li {
      counter-increment: step-counter;
      position: relative;
      padding-left: 28px;
      margin: 12px 0;
      
      &::before {
        content: counter(step-counter);
        position: absolute;
        left: 0;
        top: 0;
        width: 20px;
        height: 20px;
        background: rgba(99, 102, 241, 0.2);
        border: 1px solid rgba(99, 102, 241, 0.3);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 600;
        color: #a5b4fc;
      }
    }
  }
  
  /* Bullet list styling */
  ul {
    list-style: none;
    padding-left: 0;
    
    li {
      position: relative;
      padding-left: 16px;
      
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 8px;
        width: 5px;
        height: 5px;
        background: rgba(165, 180, 252, 0.5);
        border-radius: 50%;
      }
    }
  }
  
  /* Header styles */
  .md-h1 {
    font-size: 18px;
    font-weight: 700;
    color: #fff;
    margin: 16px 0 12px 0;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(99, 102, 241, 0.2);
    
    &:first-child {
      margin-top: 0;
    }
  }
  
  .md-h2 {
    font-size: 15px;
    font-weight: 600;
    color: #e0e7ff;
    margin: 14px 0 10px 0;
    
    &:first-child {
      margin-top: 0;
    }
  }
  
  .md-h3 {
    font-size: 13px;
    font-weight: 600;
    color: #a5b4fc;
    margin: 12px 0 8px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    
    &:first-child {
      margin-top: 0;
    }
  }
  
  /* Horizontal rule */
  .md-hr {
    border: none;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.4), transparent);
    margin: 16px 0;
  }
`;

export const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  background: rgba(30, 35, 55, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 12px;
  border-bottom-left-radius: 4px;
  align-self: flex-start;
  
  span {
    width: 6px;
    height: 6px;
    background: #a5b4fc;
    border-radius: 50%;
    animation: ${typingDots} 1.4s infinite;
    
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;

// ============================================
// SUGGESTIONS
// ============================================

export const SuggestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

export const SuggestionButton = styled.button`
  padding: 10px 14px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  color: #a5b4fc;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.15);
    border-color: rgba(99, 102, 241, 0.4);
    color: #fff;
  }
`;

// ============================================
// INPUT AREA
// ============================================

export const InputContainer = styled.div`
  padding: 16px;
  border-top: 1px solid rgba(99, 102, 241, 0.15);
  background: rgba(15, 22, 41, 0.8);
`;

export const InputWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const TextInput = styled.textarea`
  flex: 1;
  min-width: 0;
  background: rgba(25, 30, 50, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  padding: 10px 14px;
  color: #fff;
  font-size: 13px;
  font-family: inherit;
  resize: none;
  height: 44px;
  max-height: 120px;
  line-height: 1.6;
  overflow-y: auto;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
  
  &::placeholder {
    color: rgba(165, 180, 252, 0.5);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SendButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const MicButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid ${props => props.$isRecording ? 'rgba(239, 68, 68, 0.5)' : 'rgba(99, 102, 241, 0.3)'};
  background: ${props => props.$isRecording ? 'rgba(239, 68, 68, 0.15)' : 'rgba(99, 102, 241, 0.1)'};
  color: ${props => props.$isRecording ? '#ef4444' : '#a5b4fc'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  position: relative;
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  &:hover:not(:disabled) {
    background: ${props => props.$isRecording ? 'rgba(239, 68, 68, 0.25)' : 'rgba(99, 102, 241, 0.2)'};
    border-color: ${props => props.$isRecording ? 'rgba(239, 68, 68, 0.7)' : 'rgba(99, 102, 241, 0.5)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const RecordingDot = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  animation: ${recordingPulse} 1.5s infinite;
`;

export const RecordingTimer = styled.div`
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  color: #ef4444;
  white-space: nowrap;
  font-weight: 500;
`;

export const TranscribingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(165, 180, 252, 0.8);
  padding: 6px 12px;
  background: rgba(99, 102, 241, 0.1);
  border-radius: 8px;
  margin-bottom: 8px;
  
  svg {
    width: 14px;
    height: 14px;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

export const HelpTip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  margin-top: 8px;
  font-size: 11px;
  color: rgba(165, 180, 252, 0.6);
  
  kbd {
    background: rgba(99, 102, 241, 0.2);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: inherit;
  }
`;

// ============================================
// WELCOME MESSAGE
// ============================================

export const WelcomeMessage = styled.div`
  text-align: center;
  padding: 24px 20px;
  color: rgba(255, 255, 255, 0.7);
  
  .icon-wrapper {
    width: 56px;
    height: 56px;
    margin: 0 auto 16px auto;
    background: rgba(99, 102, 241, 0.08);
    border: 1px solid rgba(99, 102, 241, 0.15);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    
    img {
      width: 36px;
      height: 36px;
      object-fit: contain;
    }
  }
  
  h4 {
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 8px 0;
  }
  
  p {
    font-size: 13px;
    margin: 0 0 20px 0;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.6);
  }
`;

// ============================================
// PROACTIVE SUGGESTIONS
// ============================================

export const ProactiveSuggestionBanner = styled.div`
  margin: 0 16px 8px 16px;
  padding: 12px 14px;
  background: ${props => {
    switch (props.$type) {
      case 'warning': return 'rgba(245, 158, 11, 0.1)';
      case 'tip': return 'rgba(16, 185, 129, 0.1)';
      default: return 'rgba(99, 102, 241, 0.1)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$type) {
      case 'warning': return 'rgba(245, 158, 11, 0.3)';
      case 'tip': return 'rgba(16, 185, 129, 0.3)';
      default: return 'rgba(99, 102, 241, 0.3)';
    }
  }};
  border-radius: 10px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  animation: ${fadeIn} 0.3s ease-out;
  
  .icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => {
      switch (props.$type) {
        case 'warning': return '#f59e0b';
        case 'tip': return '#10b981';
        default: return '#6366f1';
      }
    }};
  }
  
  .content {
    flex: 1;
    
    .text {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.85);
      line-height: 1.5;
      margin-bottom: 8px;
    }
    
    .actions {
      display: flex;
      gap: 8px;
    }
  }
`;

export const SuggestionAction = styled.button`
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$primary ? css`
    background: rgba(99, 102, 241, 0.2);
    border: 1px solid rgba(99, 102, 241, 0.4);
    color: #a5b4fc;
    
    &:hover {
      background: rgba(99, 102, 241, 0.3);
    }
  ` : css`
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.5);
    
    &:hover {
      background: rgba(255, 255, 255, 0.05);
      color: rgba(255, 255, 255, 0.7);
    }
  `}
`;

// ============================================
// FLOATING ASSISTANT BUTTON
// ============================================

export const FloatingAssistantButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4);
  transition: all 0.3s ease;
  z-index: 998;
  
  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
  
  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 28px rgba(99, 102, 241, 0.5);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  ${props => props.$isOpen && css`
    background: rgba(99, 102, 241, 0.2);
    border: 2px solid rgba(99, 102, 241, 0.5);
    box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
  `}
`;

